import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Layout from "../../components/Layout";
import Breadcrumbs from "../../components/Breadcrumbs";
import { get } from "../../services/ApiService";
import "./chat.css";

function formatTime(dateStr) {
  const date = new Date(dateStr);
  const day = date.getDate().toString().padStart(2, "0");
  const month = date.toLocaleString("default", { month: "short" });
  const time = date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return `${day} ${month} ${time}`;
}

function initials(firstName, lastName) {
  return `${(firstName?.[0] || "")}${(lastName?.[0] || "")}`.toUpperCase() || "?";
}

function getDefaultDateRange() {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 6);
  start.setHours(0, 0, 0, 0);
  return [start, end];
}

function toDateParam(date) {
  if (!date) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function DateRangeInputs({ startDate, endDate, onChange }) {
  return (
    <div className="chat-report-date-range">
      <DatePicker
        selected={startDate}
        onChange={(range) => onChange(range[0] || range[1] ? range : getDefaultDateRange())}
        startDate={startDate}
        endDate={endDate}
        selectsRange
        dateFormat="dd/MM/yyyy"
        placeholderText="Select date range"
        className="form-control bg-input border-0 chat-report-datepicker"
        maxDate={new Date()}
      />
    </div>
  );
}

const TICKET_FILTERS = [
  { key: "All", label: "All" },
  { key: "Open", label: "Open" },
  { key: "Close", label: "Closed" },
  { key: "OpenGsl", label: "GSL" },
  { key: "OpenPendingPmed", label: "Pending P" },
  { key: "OpenDispatchedPmed", label: "Completed" },
];

function filterTickets(tickets, filterKey) {
  if (!filterKey || filterKey === "All") return tickets;
  if (filterKey === "Open") return tickets.filter((ticket) => ticket.status === "open");
  if (filterKey === "Close") return tickets.filter((ticket) => ticket.status === "close");
  return tickets.filter((ticket) => ticket.category === filterKey);
}

const ChatReport = () => {
  const [staff, setStaff] = useState([]);
  const [totalTickets, setTotalTickets] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [activeTab, setActiveTab] = useState("messages");
  const [messages, setMessages] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [ticketCounters, setTicketCounters] = useState({
    All: 0,
    Open: 0,
    Close: 0,
    OpenGsl: 0,
    OpenPendingPmed: 0,
    OpenDispatchedPmed: 0,
  });
  const [isActivityLoading, setIsActivityLoading] = useState(false);
  const [ticketFilter, setTicketFilter] = useState("All");
  const [dateRange, setDateRange] = useState(getDefaultDateRange);
  const [startDate, endDate] = dateRange;

  const buildRangeQuery = () => {
    const params = new URLSearchParams();
    if (startDate) params.set("startDate", toDateParam(startDate));
    if (endDate) params.set("endDate", toDateParam(endDate));
    return params.toString();
  };

  useEffect(() => {
    if (!startDate || !endDate) return;
    const fetchStaff = async () => {
      try {
        const res = await get(`api/messages/staff-users?${buildRangeQuery()}`);
        const users = res?.users || [];
        setStaff(users);
        setTotalTickets(res?.totalTickets || 0);
      } catch (err) {
        console.error("Failed to fetch staff users:", err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchStaff();
  }, [startDate, endDate]);

  useEffect(() => {
    if (!selectedEmail || !startDate || !endDate) return;
    const fetchActivity = async () => {
      setIsActivityLoading(true);
      setMessages([]);
      setTickets([]);
      setTicketCounters({
        All: 0,
        Open: 0,
        Close: 0,
        OpenGsl: 0,
        OpenPendingPmed: 0,
        OpenDispatchedPmed: 0,
      });
      setTicketFilter("All");
      try {
        const res = await get(
          `api/messages/staff-activity?email=${encodeURIComponent(selectedEmail)}&${buildRangeQuery()}`
        );
        setMessages(res?.messages || []);
        setTickets(res?.tickets || []);
        setTicketCounters({
          All: res?.ticketCounters?.All || 0,
          Open: res?.ticketCounters?.Open || 0,
          Close: res?.ticketCounters?.Close || 0,
          OpenGsl: res?.ticketCounters?.OpenGsl || 0,
          OpenPendingPmed: res?.ticketCounters?.OpenPendingPmed || 0,
          OpenDispatchedPmed: res?.ticketCounters?.OpenDispatchedPmed || 0,
        });
      } catch (err) {
        console.error("Failed to fetch staff activity:", err);
      } finally {
        setIsActivityLoading(false);
      }
    };
    fetchActivity();
  }, [selectedEmail, startDate, endDate]);

  const selectedUser = staff.find((u) => u.email === selectedEmail) || null;
  const visibleTickets = filterTickets(tickets, ticketFilter);

  return (
    <Layout>
      {/* Page header */}
      <div className="row mb-3 align-items-center justify-content-between">
        <div className="col-sm-6 mb-2 mb-sm-0">
          <h2 className="fs-4 mb-0">Chat Report</h2>
          <Breadcrumbs />
        </div>
        <div className="col-sm-6 d-flex justify-content-end align-items-center gap-4 flex-wrap">
          <div className="text-end">
            <div className="fw-semibold">{staff.length}</div>
            <small className="text-muted">Staff members</small>
          </div>
          <div className="text-end">
            <div className="fw-semibold">{staff.reduce((s, u) => s + (u.messageCount || 0), 0).toLocaleString()}</div>
            <small className="text-muted">Messages sent</small>
          </div>
          <div className="text-end">
            <div className="fw-semibold">{totalTickets.toLocaleString()}</div>
            <small className="text-muted">All tickets</small>
          </div>
        </div>
      </div>

      {isLoading && (
        <div className="text-center text-muted py-5">
          <div className="spinner-border spinner-border-sm me-2" />
          Loading staff...
        </div>
      )}

      {!isLoading && (
        <div className="chat-report-container">

          {/* Left — staff list */}
          <div className="chat-report-sidebar">
            <div className="chat-report-sidebar-header">
              <small className="text-muted text-uppercase" style={{ letterSpacing: "0.06em", fontWeight: 600, fontSize: "11px" }}>
                Members
              </small>
            </div>
            {staff.map((user) => {
              const isSelected = user.email === selectedEmail;
              return (
                <div
                  key={user.email}
                  className={`chat-report-user-row ${isSelected ? "chat-report-user-row--selected" : ""}`}
                  onClick={() => { setSelectedEmail(user.email); setActiveTab("messages"); }}
                >
                  <div className="chat-report-avatar">
                    {initials(user.firstName, user.lastName)}
                  </div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div className="chat-report-user-name">
                      {user.firstName} {user.lastName}
                    </div>
                    <div className="chat-report-user-email text-truncate">{user.email}</div>
                    <div className="chat-report-user-stats">
                      <span>{user.messageCount} msgs</span>
                      <span>{user.ticketCount} tickets</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Right — detail panel */}
          <div className="chat-report-detail">
            {selectedUser && (
              <>
                {/* Selected user header */}
                <div className="chat-report-detail-header">
                  <div className="d-flex align-items-center gap-3">
                    <div className="chat-report-avatar chat-report-avatar--lg">
                      {initials(selectedUser.firstName, selectedUser.lastName)}
                    </div>
                    <div>
                      <div className="fw-semibold" style={{ fontSize: "15px" }}>
                        {selectedUser.firstName} {selectedUser.lastName}
                      </div>
                      <div className="text-muted" style={{ fontSize: "13px" }}>{selectedUser.email}</div>
                    </div>
                  </div>
                  <div className="d-flex gap-4">
                    <div className="text-end">
                      <div className="fw-semibold">{selectedUser.messageCount ?? 0}</div>
                      <small className="text-muted">Messages</small>
                    </div>
                    <div className="text-end">
                      <div className="fw-semibold">{selectedUser.ticketCount ?? 0}</div>
                      <small className="text-muted">Tickets</small>
                    </div>
                  </div>
                </div>

                {/* Tabs */}
                <div className="chat-report-tabs">
                  <div className="chat-report-tab-buttons">
                    <button
                      className={`chat-report-tab-btn ${activeTab === "messages" ? "active" : ""}`}
                      onClick={() => setActiveTab("messages")}
                    >
                      Messages
                    </button>
                    <button
                      className={`chat-report-tab-btn ${activeTab === "tickets" ? "active" : ""}`}
                      onClick={() => setActiveTab("tickets")}
                    >
                      Tickets
                    </button>
                  </div>
                  <DateRangeInputs
                    startDate={startDate}
                    endDate={endDate}
                    onChange={setDateRange}
                  />
                </div>

                {/* Tab content */}
                <div className="chat-report-content">

                  {/* Messages tab */}
                  {activeTab === "messages" && (
                    <div className="chat-report-messages">
                      {isActivityLoading && (
                        <div className="text-center text-muted py-4">
                          <div className="spinner-border spinner-border-sm me-2" />
                          Loading...
                        </div>
                      )}
                      {!isActivityLoading && messages.length === 0 && (
                        <p className="text-muted text-center py-4" style={{ fontSize: "13px" }}>No messages in this date range.</p>
                      )}
                      {!isActivityLoading && messages.map((msg) => {
                        const orderId = msg.order?.orderId || null;
                        const orderMongoId = msg.order?._id || null;
                        return (
                          <div key={msg._id} className="chat-report-msg chat-report-msg--out">
                            <div className="chat-report-msg-sender">
                              {selectedUser.firstName} {selectedUser.lastName}
                            </div>
                            <div className="chat-report-msg-bubble chat-report-msg-bubble--out" style={{ position: "relative" }}>
                              {orderId && (
                                <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 4 }}>
                                  <small style={{ opacity: 0.85 }}>
                                    {orderMongoId ? (
                                      <a href={`/orders/${orderMongoId}`} style={{ color: "white", textDecoration: "underline" }}>
                                        {orderId}
                                      </a>
                                    ) : orderId}
                                  </small>
                                </div>
                              )}
                              <p className="mb-1" style={{ fontSize: "14px" }}>{msg.body}</p>
                              <small>{formatTime(msg.creationDate)}</small>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Tickets tab */}
                  {activeTab === "tickets" && (
                    <div className="chat-report-tickets">
                      {isActivityLoading && (
                        <div className="text-center text-muted py-4">
                          <div className="spinner-border spinner-border-sm me-2" />
                          Loading...
                        </div>
                      )}
                      {!isActivityLoading && (
                      <>
                      <div className="chat-report-ticket-counters">
                        {TICKET_FILTERS.map((filter) => (
                          <button
                            key={filter.key}
                            type="button"
                            className={`chat-report-ticket-counter ${ticketFilter === filter.key ? "chat-report-ticket-counter--active" : ""}`}
                            onClick={() => setTicketFilter(filter.key)}
                          >
                            <div className="chat-report-ticket-counter-value">
                              {ticketCounters[filter.key] || 0}
                            </div>
                            <div className="chat-report-ticket-counter-label">{filter.label}</div>
                          </button>
                        ))}
                      </div>
                      <p className="text-muted mb-3" style={{ fontSize: "13px" }}>
                        {visibleTickets.length} ticket{visibleTickets.length !== 1 ? "s" : ""} for {selectedUser.firstName} {selectedUser.lastName}
                      </p>
                      {visibleTickets.length === 0 && (
                        <p className="text-muted text-center py-4" style={{ fontSize: "13px" }}>No tickets in this date range.</p>
                      )}
                      {visibleTickets.length > 0 && (
                        <table className="table table-sm table-hover mb-0">
                          <thead>
                            <tr>
                              <th>Ticket</th>
                              <th>Customer</th>
                              <th>Status</th>
                              <th className="text-end">Closed at</th>
                            </tr>
                          </thead>
                          <tbody>
                            {visibleTickets.map((t) => {
                              const orderMongoId = t.order?._id || t.order || null;
                              return (
                              <tr key={t.ticketNo}>
                                <td className="fw-medium">
                                  {orderMongoId ? (
                                    <Link to={`/orders/${orderMongoId}`} className="chat-report-ticket-link">
                                      {t.ticketNo}
                                    </Link>
                                  ) : (
                                    t.ticketNo
                                  )}
                                </td>
                                <td className="text-muted">{t.customerID}</td>
                                <td className="text-capitalize">{t.status === "close" ? "Closed" : "Open"}</td>
                                <td className="text-end text-muted">{t.closedAt ? formatTime(t.closedAt) : "—"}</td>
                              </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      )}
                      </>
                      )}
                    </div>
                  )}

                </div>
              </>
            )}
          </div>

        </div>
      )}
    </Layout>
  );
};

export default ChatReport;
